import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const D3Tree = ({ data }) => {
    const svgRef = useRef();
    const containerRef = useRef();

    useEffect(() => {
        if (!data) return;

        // Clear previous content
        d3.select(svgRef.current).selectAll("*").remove();

        // Get container dimensions
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight;

        // Set dimensions and margins
        const margin = { top: 20, right: 90, bottom: 30, left: 90 };
        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;

        // Create the SVG container
        const svg = d3.select(svgRef.current)
            .attr("width", containerWidth)
            .attr("height", containerHeight)
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Create the tree layout
        const tree = d3.tree()
            .size([height, width]);

        // Transform flat data into hierarchical structure
        const buildTree = (nodes) => {
            const nodeMap = {};
            const tree = [];

            nodes.forEach(node => {
                nodeMap[node.Id] = { ...node, children: [] };
            });

            nodes.forEach(node => {
                if (node.ParentId) {
                    const parent = nodeMap[node.ParentId];
                    if (parent) {
                        parent.children.push(nodeMap[node.Id]);
                    }
                } else {
                    tree.push(nodeMap[node.Id]);
                }
            });

            return d3.hierarchy(tree[0]);
        };

        // Initialize the tree
        const root = buildTree(data);
        root.x0 = height / 2;
        root.y0 = 0;

        // Collapse all nodes except the root
        root.children.forEach(collapse);

        // Update the tree
        update(root);

        // Function to collapse a node
        function collapse(d) {
            if (d.children) {
                d._children = d.children;
                d._children.forEach(collapse);
                d.children = null;
            }
        }

        // Function to expand a node
        function expand(d) {
            if (d._children) {
                d.children = d._children;
                d._children = null;
            }
        }

        // Update function
        function update(source) {
            // Compute the new tree layout
            const nodes = tree(root).descendants();
            const links = tree(root).links();

            // Calculate dynamic spacing based on container width
            const nodeSpacing = Math.max(width / 8, 100) * 3; // Tripled spacing with minimum of 300px or 3/8 of container width
            nodes.forEach(d => { d.y = d.depth * nodeSpacing; });

            // Update the nodes
            const node = svg.selectAll(".node")
                .data(nodes, d => d.data.Id);

            // Enter any new nodes at the parent's previous position
            const nodeEnter = node.enter().append("g")
                .attr("class", "node")
                .attr("transform", d => `translate(${source.y0},${source.x0})`)
                .on("click", (event, d) => {
                    if (d.children) {
                        d._children = d.children;
                        d.children = null;
                    } else {
                        d.children = d._children;
                        d._children = null;
                    }
                    update(d);
                });

            // Add circles for the nodes with dynamic size
            const nodeRadius = Math.min(width / 100, 10); // Scale node size with container width
            nodeEnter.append("circle")
                .attr("r", nodeRadius)
                .style("fill", "#4a90e2")
                .style("stroke", "#fff")
                .style("stroke-width", "2px")
                .style("cursor", "pointer");

            // Add expand/collapse indicators with dynamic positioning
            nodeEnter.append("text")
                .attr("dy", ".35em")  // Center vertically in circle
                .attr("x", 0)         // Center horizontally in circle
                .style("text-anchor", "middle")  // Center align text
                .text(d => d._children ? "+" : (d.children ? "-" : ""))
                .style("font-size", `${nodeRadius * 1.2}px`)  // Slightly smaller to fit in circle
                .style("font-weight", "bold")
                .style("fill", "#fff");  // White text for better contrast

            // Add node labels with dynamic positioning
            nodeEnter.append("text")
                .attr("dy", "-2.5em")  // Keep name above the circle
                .attr("x", 0)          // Center text above circle
                .style("text-anchor", "middle")  // Center align text
                .text(d => `${d.data.Name}${d.data.Code ? ` (${d.data.Code})` : ''}`)
                .style("font-size", `${nodeRadius * 1.2}px`)
                .style("font-family", "Arial");

            // Add unit type labels
            //nodeEnter.append("text")
              //  .attr("dy", "1.5em")
                //.attr("x", 25)
                //   .style("text-anchor", "start")
               //  .text(d => d.data.UnitTypeCode)
               //  .style("font-size", "10px")
               //  .style("fill", "#666")
                // .style("font-family", "Arial");

            // Update the node positions
            const nodeUpdate = nodeEnter.merge(node);
            nodeUpdate.transition()
                .duration(750)
                .attr("transform", d => `translate(${d.y},${d.x})`);

            // Update the node attributes and style
            nodeUpdate.select("circle")
                .attr("r", nodeRadius)
                .style("fill", "#4a90e2");

            // Update the expand/collapse indicators
            nodeUpdate.select("text:first-of-type")
                .text(d => d._children ? "+" : (d.children ? "-" : ""));

            // Remove any exiting nodes
            const nodeExit = node.exit().transition()
                .duration(750)
                .attr("transform", d => `translate(${source.y},${source.x})`)
                .remove();

            // Update the links
            const link = svg.selectAll(".link")
                .data(links, d => d.target.data.Id);

            // Enter any new links at the parent's previous position
            const linkEnter = link.enter().append("path")
                .attr("class", "link")
                .attr("d", d => {
                    const o = { x: source.x0, y: source.y0 };
                    return d3.linkHorizontal()
                        .x(d => d.y)
                        .y(d => d.x)({ source: o, target: o });
                })
                .style("fill", "none")
                .style("stroke", "#999")
                .style("stroke-width", "1px")
                .style("stroke-opacity", "0.6");

            // Update links with dynamic curve
            const linkUpdate = linkEnter.merge(link);
            linkUpdate.transition()
                .duration(750)
                .attr("d", d3.linkHorizontal()
                    .x(d => d.y)
                    .y(d => d.x))
                .style("fill", "none")
                .style("stroke", "#999")
                .style("stroke-width", "1px")
                .style("stroke-opacity", "0.6");

            // Remove any exiting links
            link.exit().transition()
                .duration(750)
                .attr("d", d => {
                    const o = { x: source.x, y: source.y };
                    return d3.linkHorizontal()
                        .x(d => d.y)
                        .y(d => d.x)({ source: o, target: o });
                })
                .remove();

            // Store the old positions for transition
            nodes.forEach(d => {
                d.x0 = d.x;
                d.y0 = d.y;
            });
        }

        // Add zoom behavior
        const zoom = d3.zoom()
            .scaleExtent([0.5, 2])
            .on("zoom", (event) => {
                svg.attr("transform", event.transform);
            });

        d3.select(svgRef.current).call(zoom);

    }, [data]);

    return (
        <div ref={containerRef} style={{ width: '100%', height: 'calc(100vh - 200px)', overflow: 'hidden' }}>
            <svg ref={svgRef} style={{ width: '100%', height: '100%' }}></svg>
        </div>
    );
};

export { D3Tree }; 